<template>
	<ul class="base-pagination">
		<li
			class="base-pagination__item base-pagination__item--prev"
			:class="{ 'base-pagination__item--disabled': !hasPrev }"
		>
			<base-button minimal :disabled="!hasPrev" @click="prev">&lsaquo;</base-button>
		</li>
		<li
			v-for="page in leftGroup"
			:key="page.number"
			class="base-pagination__item"
			:class="{
				'base-pagination__item--current': isCurrent(page),
			}"
		>
			<base-button minimal @click="to(page)">{{ page.number }}</base-button>
		</li>
		<li v-if="hasFirstEllipsis" class="base-pagination__item base-pagination__item--ellipsis">
			&hellip;
		</li>
		<li
			v-for="page in centerGroup"
			:key="page.number"
			class="base-pagination__item"
			:class="{
				'base-pagination__item--current': isCurrent(page),
			}"
		>
			<base-button minimal @click="to(page)">{{ page.number }}</base-button>
		</li>
		<li v-if="hasSecondEllipsis" class="base-pagination__item base-pagination__item--ellipsis">
			&hellip;
		</li>
		<li
			v-for="page in rightGroup"
			:key="page.number"
			class="base-pagination__item"
			:class="{
				'base-pagination__item--current': isCurrent(page),
			}"
		>
			<base-button minimal @click="to(page)">{{ page.number }}</base-button>
		</li>
		<li
			class="base-pagination__item base-pagination__item--next"
			:class="{ 'base-pagination__item--disabled': !hasNext }"
		>
			<base-button minimal :disabled="!hasNext" @click="next">&rsaquo;</base-button>
		</li>
	</ul>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

interface Page {
	number: number;
}

export default defineComponent({
	props: {
		total: {
			type: Number,
			required: true,
		},
		current: {
			type: Number,
			required: true,
		},
		size: {
			type: Number,
			default: 8,
		},
	},

	emits: ['change'],

	computed: {
		hasPrev(): boolean {
			return this.current > 1;
		},
		hasNext(): boolean {
			return this.current < this.total;
		},
		hasFirstEllipsis(): boolean {
			return this.isOverflown && this.min > 2;
		},
		hasSecondEllipsis(): boolean {
			return this.isOverflown && this.max < this.total - 1;
		},
		min(): number {
			const min = this.isOverflown
				? Math.min(
						this.total - (this.size - 2),
						Math.max(1, this.current - Math.floor((this.size - 3) / 2)),
					)
				: 1;

			if (min == 2) {
				return 1;
			}

			return min;
		},
		max(): number {
			const max = this.isOverflown ? Math.min(this.total, this.min + this.size - 2) : this.total;

			if (max == this.total - 1) {
				return this.total;
			}

			return max;
		},
		leftGroup(): Array<Page> {
			const pages = new Array<Page>();

			if (this.hasFirstEllipsis) {
				pages.push({
					number: 1,
				});
			}

			return pages;
		},
		centerGroup(): Array<Page> {
			const pages = new Array<Page>();

			for (let number = this.min; number <= this.max; number++) {
				pages.push({
					number,
				});
			}

			return pages;
		},
		rightGroup(): Array<Page> {
			const pages = new Array<Page>();

			if (this.hasSecondEllipsis) {
				pages.push({
					number: this.total,
				});
			}

			return pages;
		},
		isOverflown(): boolean {
			return this.total > this.size;
		},
	},

	methods: {
		prev(): void {
			if (this.hasPrev) {
				this.$emit('change', this.current - 1);
			}
		},
		next(): void {
			if (this.hasNext) {
				this.$emit('change', this.current + 1);
			}
		},
		to(page: Page): void {
			this.$emit('change', page.number);
		},
		isCurrent(page: Page): boolean {
			return page.number == this.current;
		},
	},
});
</script>

<style lang="scss">
.base-pagination {
	display: inline-flex;
	align-items: center;
	border: 1px solid #eaeaea;
	border-radius: 0.4rem;

	&__item {
		&--current {
			text-decoration: underline;
			pointer-events: none;
			background: #f0f0f0;
		}

		&--prev {
			border-right: 1px solid #eaeaea;

			.base-button {
				padding-right: 1.2rem;
				padding-left: 1.2rem;
			}
		}

		&--next {
			border-left: 1px solid #eaeaea;

			.base-button {
				padding-right: 1.2rem;
				padding-left: 1.2rem;
			}
		}

		&--ellipsis {
			padding: 0 1rem;
			color: #999;
		}

		.base-button {
			border-radius: 0;

			&:disabled {
				pointer-events: none;
				opacity: 0.5;
			}
		}

		&:first-child {
			.base-button {
				border-radius: 0.4rem 0 0 0.4rem;
			}
		}

		&:last-child {
			.base-button {
				border-radius: 0 0.4rem 0.4rem 0;
			}
		}
	}
}
</style>
